import { __awaiter } from '../node_modules/tslib/tslib.es6.js';
import { createPredicate } from './condition.js';

const SCRIPT_SRC = 'https://c.amazon-adsystem.com/aat/amzn.js';
const SCRIPT_ID = 'amzn-pixel';
const PROVIDER = 'AmazonAds';
let debug = false;
let initialized = false;
const log = (...args) => debug &&
    // eslint-disable-next-line no-console
    console.info(...['[amazonAds]'].concat(args));
/**
 * Check if Amazon Ads script is loaded
 */
const isLoaded = () => typeof window !== 'undefined' && !!window.amzn;
/**
 * Load the Amazon Ads tracking script
 */
const loadScript = () => {
    return new Promise((resolve, reject) => {
        if (isLoaded()) {
            log('Amazon Ads script already loaded');
            resolve();
            return;
        }
        if (typeof window === 'undefined') {
            reject(new Error('Cannot load Amazon Ads script in non-browser environment'));
            return;
        }
        // Check if script tag already exists
        if (document.getElementById(SCRIPT_ID)) {
            log('Amazon Ads script tag already exists');
            resolve();
            return;
        }
        log('Loading Amazon Ads script...');
        const script = document.createElement('script');
        script.src = SCRIPT_SRC;
        script.id = SCRIPT_ID;
        script.async = true;
        script.onload = () => {
            log('Amazon Ads script loaded successfully');
            resolve();
        };
        script.onerror = (error) => {
            log('Failed to load Amazon Ads script', error);
            reject(new Error('Failed to load Amazon Ads script'));
        };
        const firstScript = document.getElementsByTagName('script')[0];
        (firstScript === null || firstScript === void 0 ? void 0 : firstScript.parentNode)
            ? firstScript.parentNode.insertBefore(script, firstScript)
            : document.head.appendChild(script);
    });
};
/**
 * Initialize Amazon Ads tracking
 */
const initializeAmazonAds = (brandId, pixel) => __awaiter(void 0, void 0, void 0, function* () {
    if (!pixel.region || !pixel.amazonTagId) {
        log('Amazon Ads config is missing required fields', pixel);
        return;
    }
    if (initialized) {
        log('Amazon Ads already initialized');
        return;
    }
    try {
        yield loadScript();
        if (!window.amzn) {
            log('Amazon Ads script loaded but amzn object not available');
            return;
        }
        // Set region and add tag
        window.amzn('setRegion', pixel.region);
        window.amzn('addTag', pixel.amazonTagId);
        initialized = true;
        log('Amazon Ads initialized with config', { brandId, region: pixel.region, amazonTagId: pixel.amazonTagId });
    }
    catch (error) {
        log('Failed to initialize Amazon Ads', error);
    }
});
/**
 * Track an Amazon Ads event
 */
const trackAmazonEvent = (eventName, params) => {
    if (!isLoaded()) {
        log('Cannot track event - Amazon Ads not initialized');
        return;
    }
    try {
        if (params) {
            window.amzn('trackEvent', eventName, params);
        }
        else {
            window.amzn('trackEvent', eventName);
        }
        log(`Tracked ${eventName} event`, params);
    }
    catch (error) {
        log(`Failed to track ${eventName} event`, error);
    }
};
/**
 * Initialize Amazon Ads with configuration
 */
const init = (config) => {
    return initializeAmazonAds(config.brandId, {
        pixelProvider: PROVIDER,
        region: config.region,
        amazonTagId: config.amazonTagId
    });
};
const amazonAds = {
    enableDebug: () => { debug = true; },
    track({ brandId, pixels, eventName, eventType, eventPayload }) {
        // Process each Amazon Ads pixel
        pixels.forEach((pixel) => __awaiter(this, void 0, void 0, function* () {
            // Apply event filter if present
            if (pixel.eventFilter) {
                const predicate = createPredicate(pixel.eventFilter);
                if (!predicate(eventPayload)) {
                    log('eventFilter not matched', pixel.eventFilter, pixel.pixelId, eventName, eventPayload);
                    return;
                }
            }
            // Initialize Amazon Ads
            yield initializeAmazonAds(brandId, pixel);
            if (!initialized)
                return;
            // Track the appropriate events
            switch (eventType) {
                default:
                    trackAmazonEvent(eventName, eventPayload);
                    break;
            }
        }));
    },
    // For backward compatibility
    init,
    isLoaded
};

export { amazonAds as default };
//# sourceMappingURL=amazonAds.js.map
